import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CLINIC_SETTINGS_UPDATED_EVENT, type ClinicSettingsUpdatedPayload } from '@valiosascrm/shared';
import { sessionSocketListeners } from '@/lib/session-socket-listeners';
import { useSessionBlocked } from '@/stores/sessionTransition';
import { getSocket } from '@/lib/socket';
import { useAuthStore } from '@/stores/auth';
import { applyClinicNameToSession, applyTenantSettingsToSession } from '@/lib/tenant-settings-sync';

/**
 * RC-065: outra aba (ou outro computador da clínica) salvou Configurações. Atualiza o retrato
 * de `tenant.settings` desta sessão — o fuso da grade troca na hora, sem novo login — e
 * invalida a query da tela de Configurações, se estiver aberta.
 */
export function useRealtimeClinicSettings(token: string | null) {
  const queryClient = useQueryClient();
  const paused = useSessionBlocked();
  const userTenantId = useAuthStore((s) => s.user?.tenant?.id ?? null);

  useEffect(() => {
    if (paused || !token) return;
    const socket = getSocket({ token });
    if (!socket) return;
    const listeners = sessionSocketListeners(socket);

    const handleUpdated = (payload: Partial<ClinicSettingsUpdatedPayload> | undefined) => {
      // O servidor já escopa por room; o handler ainda recusa payload de outro tenant.
      if (payload?.tenantId && userTenantId && payload.tenantId !== userTenantId) return;
      // Story 67.8 — troca do nome da clínica: seletor do topo, Perfil e "Todas as clínicas".
      if (applyClinicNameToSession(payload?.tenantId ?? userTenantId, payload?.name)) {
        queryClient.invalidateQueries({ queryKey: ['consolidated-overview'] });
      }
      if (applyTenantSettingsToSession(payload?.settings)) {
        queryClient.invalidateQueries({ queryKey: ['clinic-settings'] });
      }
    };

    listeners.on(CLINIC_SETTINGS_UPDATED_EVENT, handleUpdated);
    return () => listeners.off(CLINIC_SETTINGS_UPDATED_EVENT, handleUpdated);
  }, [paused, token, queryClient, userTenantId]);
}
